import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Box, Grid, Paper, Typography, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, getTableSyncStatus } from '../services/api';
import LastSyncedLabel from '../components/common/LastSyncedLabel';
import { useAuth } from '../contexts/AuthContext';
import { FilterRequest } from '../types/api';
import UtilizationTopFilters from '../components/filters/page/UtilizationTopFilters';
import { getResourceMetrics, getOrgRanking } from '../services/analyticsApi';
import { CACHE_TIME } from '../constants/QueryConstants';
import {
  LazyUtilizationMetricCardsSection,
  LazyHeadcountMetricsSection,
  LazyUtilizationAnalysisSection,
  LazyOrganizationalAnalytics,
  LazySolverInsightsTabbedPanel,
} from '../components/LazyComponents';
import { getDefaultDuration } from '../utils/DateUtils';
import { hasFiltersChanged } from '../utils/FilterUtils';
import { UtilizationEmployeeSectionSkeleton } from '../components/utilization/UtilizationInsightsSkeletons';
import UtilizationActionableInsights, {
  UTILIZATION_SOLVER_INSIGHTS_SCROLL_ID,
  getUtilizationInsightTableFilterPatch,
  type UtilizationInsightCardId,
} from '../components/utilization/UtilizationActionableInsights';

const DELIVERY_BU_TYPE = 'Delivery';

const UtilizationInsightsPage: React.FC = () => {
  const { user, isLoading: userLoading } = useAuth();
  const [filters, setFilters] = useState<FilterRequest>({
    ...getDefaultDuration(),
    employee_status: 'ACTIVE',
    business_units: [],
    sub_bus: [],
    delivery_managers: [],
    squads: [],
    employee_names: [],
    managers: [],
    project_codes: [],
    project_status: [],
    designations: [],
    employee_types: [],
    countries: [],
    locations: [],
    pillars: [],
    work_mode_profiles: [],
  });

  const [activeFilters, setActiveFilters] = useState<FilterRequest>(filters);
  const [insightTableApply, setInsightTableApply] = useState<{
    requestId: number;
    cardId: UtilizationInsightCardId;
    emailAllowList: string[];
  } | null>(null);
  const hasInitializedRef = useRef(false);

  const activeFiltersSignature = useMemo(() => JSON.stringify(activeFilters), [activeFilters]);

  // On first load only: when FilterPanel auto-selects all BUs and sub_bus, sync to activeFilters so queries run without pressing Refresh
  // After initial sync, user must click Refresh to apply filter changes
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (
      filters.business_units && filters.business_units.length > 0 &&
      filters.sub_bus && filters.sub_bus.length > 0
    ) {
      setActiveFilters(filters);
      hasInitializedRef.current = true;
    }
  }, [filters]);

  useEffect(() => {
    setInsightTableApply(null);
  }, [activeFiltersSignature]);

  const handleNavigateInsight = useCallback(
    (cardId: UtilizationInsightCardId, matchingEmails: string[]) => {
      const patch = getUtilizationInsightTableFilterPatch(cardId, matchingEmails);
      setInsightTableApply({
        requestId: Date.now(),
        cardId,
        emailAllowList: patch.emailAllowList,
      });
      requestAnimationFrame(() => {
        setTimeout(() => {
          document.getElementById(UTILIZATION_SOLVER_INSIGHTS_SCROLL_ID)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 320);
      });
    },
    []
  );

  const deliveryBusinessUnitOptions = useMemo(() => {
    const list = user?.business_units?.filter((bu) => bu.bu_type === DELIVERY_BU_TYPE) ?? [];
    return list.map((bu) => bu.name);
  }, [user?.business_units]);

  // Logic to determine if queries should run
  const queriesEnabled = !!user && 
    deliveryBusinessUnitOptions.length > 0 && 
    !!(activeFilters.business_units && activeFilters.business_units.length > 0);

  // Individual Queries
  const { data: resourceMetricsData, isLoading: resourceMetricsLoading, refetch: refetchResourceMetrics } = useQuery({
    queryKey: ['analytics-resource', activeFilters],
    queryFn: () => getResourceMetrics(activeFilters),
    staleTime: CACHE_TIME,
    enabled: queriesEnabled,
  });

  const { data: monthlyHeadcountData, isLoading: isLoadingMonthly } = useQuery({
    queryKey: ['monthly-headcount-trends', activeFilters],
    queryFn: () => dashboardApi.getMonthlyHeadcountTrends(activeFilters || {}),
    enabled: queriesEnabled,
  });

  const { data: monthlyAttritionData, isLoading: isLoadingAttrition } = useQuery({
    queryKey: ['monthly-attrition-trends', activeFilters],
    queryFn: () => dashboardApi.getMonthlyAttritionTrends(activeFilters || {}),
    enabled: queriesEnabled,
  });

  const { data: orgRankingData, isLoading: isLoadingOrgRanking, refetch: refetchOrgRanking } = useQuery({
    queryKey: ['analytics-org-ranking', activeFilters],
    queryFn: () => getOrgRanking(activeFilters),
    staleTime: CACHE_TIME,
    enabled: queriesEnabled,
  });

  const { data: projectsMetricsData, isLoading: isLoadingProjectsMetrics } = useQuery({
    queryKey: ['projects-metrics', activeFilters],
    queryFn: () => dashboardApi.getProjectsProjectMetrics(activeFilters || {}),
    staleTime: CACHE_TIME,
    enabled: queriesEnabled,
  });

  const projectsCount = projectsMetricsData?.status_counts.total_projects ?? 0;

  const handleFiltersChange = (newFilters: FilterRequest) => setFilters(newFilters);

  const handleRefresh = () => {
    setActiveFilters(filters);
    if (filters.business_units && filters.business_units.length > 0) {
      refetchResourceMetrics();
      refetchOrgRanking();
    }
  };

  const { data: syncData, isLoading: syncLoading } = useQuery({
    queryKey: ['sync-status'],
    queryFn: getTableSyncStatus,
    staleTime: 10 * 60 * 1000,
    enabled: !!user,
  });

  const filtersHaveChanged = hasFiltersChanged(filters, activeFilters);
  const userHasNoDeliveryBUs = !userLoading && deliveryBusinessUnitOptions.length === 0;

  /** Same pattern as Projects: treat “waiting on user / filters” like loading so sections stay mounted with skeletons */
  const blockDataFetch = !queriesEnabled;
  const metricsShellLoading = userLoading || blockDataFetch || resourceMetricsLoading;

  // Permission Error State
  if (userHasNoDeliveryBUs) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Alert severity="info" sx={{ maxWidth: 600 }}>
          <Typography variant="h6">No Delivery Business Units Assigned</Typography>
          <Typography variant="body2">Please contact your administrator to assign Delivery Business Units.</Typography>
        </Alert>
      </Box>
    );
  }

  const hasAnyData =
    !!resourceMetricsData ||
    !!orgRankingData ||
    (Array.isArray(monthlyHeadcountData) && monthlyHeadcountData.length > 0) ||
    (Array.isArray(monthlyAttritionData) && monthlyAttritionData.length > 0);

  const dashboardQueriesSettled =
    queriesEnabled &&
    !resourceMetricsLoading &&
    !isLoadingMonthly &&
    !isLoadingAttrition &&
    !isLoadingOrgRanking;

  const showEmptyState =
    !userLoading &&
    queriesEnabled &&
    dashboardQueriesSettled &&
    !hasAnyData;

  const sectionSx = {
    p: 2,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    boxShadow: 'none',
  };

  return (
    <Box sx={{ px: 2, pt: 2, pb: 2 }}>
      <Box sx={{ mb: 2 }}>
        <UtilizationTopFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onRefresh={handleRefresh}
          hasUnsavedChanges={filtersHaveChanged}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <LastSyncedLabel isoTimestamp={syncData?.utilization} isLoading={syncLoading} />
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={sectionSx}>
            <LazyUtilizationMetricCardsSection
              filters={activeFilters}
              ResourceMetricsData={resourceMetricsData}
              orgRankingData={orgRankingData}
              isLoading={metricsShellLoading}
              projectsCount={projectsCount}
              isLoadingProjectsCount={blockDataFetch || isLoadingProjectsMetrics}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={sectionSx}>
            <LazyHeadcountMetricsSection
              data={resourceMetricsData}
              filters={activeFilters}
              monthlyHeadcountData={monthlyHeadcountData}
              monthlyAttritionData={monthlyAttritionData}
              isAllocationLoading={metricsShellLoading}
              isLoadingMonthly={blockDataFetch || isLoadingMonthly}
              isLoadingAttrition={blockDataFetch || isLoadingAttrition}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={sectionSx}>
            <LazyUtilizationAnalysisSection
              data={resourceMetricsData}
              filters={activeFilters}
              isLoading={metricsShellLoading}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={{ ...sectionSx, py: 1, px: 0, borderRadius: 0 }}>
            <LazyOrganizationalAnalytics data={orgRankingData} filters={activeFilters} />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={0} sx={sectionSx}>
            <UtilizationActionableInsights
              filters={activeFilters}
              queriesEnabled={queriesEnabled}
              onNavigateWithInsight={handleNavigateInsight}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} id={UTILIZATION_SOLVER_INSIGHTS_SCROLL_ID}>
          <Paper
            elevation={0}
            sx={{
              ...sectionSx,
              p: 0,
              border: 'none',
            }}
          >
            {userLoading ? (
              <UtilizationEmployeeSectionSkeleton />
            ) : (
              <LazySolverInsightsTabbedPanel
                filters={activeFilters}
                insightTableApply={insightTableApply}
                onClearInsightFilter={() => setInsightTableApply(null)}
              />
            )}
          </Paper>
        </Grid>

        {showEmptyState && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ ...sectionSx, p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No dashboard data available
              </Typography>
              <Typography variant="body2">
                Please select filters and click refresh.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default UtilizationInsightsPage;